import { selectClassificationLoading, selectCommunityResultHistory } from '@classification-testing/state/selectors';
import { Illustration, IllustrationType, IllustrationLayout, SectionHeader } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { Shimmer, Card } from '@soteria/design-system-ve';
import { ClassificationResultItem } from '@soteria-ui/src/classification-testing/components';
import { ClassificationResultItemV2 } from '@soteria-ui/src/classification-testing/components/ClassificationResultItemV2';
import { useAppSelector } from '@store';

import * as styles from './ClassificationResultsSection.css';

interface ClassificationResultsSectionProps {
  communityId?: string;
  communityName: string;
}

/**
 * Result History Section component
 */
export function ClassificationResultsSection({ communityId, communityName }: ClassificationResultsSectionProps) {
  const isLoading = useAppSelector(selectClassificationLoading);
  const results = useAppSelector(selectCommunityResultHistory(communityId || ''));
  const isV2CardDesign = useIsFeatureEnabled(FEATURE_FLAGS.V2_CARD_DESIGN);

  const emptyState = results.length === 0 && !isLoading && (
    <div className={styles.emptyStateContainer}>
      <Illustration
        description={`Enter content and click "Classify" to begin testing for ${communityName}.`}
        layout={IllustrationLayout.HORIZONTAL}
        title="No classification results yet"
        type={IllustrationType.EMPTY}
      />
    </div>
  );

  return (
    <div>
      <SectionHeader description="View result history" title="Classification Results" />
      {isV2CardDesign ? (
        <div className={styles.containerV2}>
          {isLoading && <Shimmer className={styles.shimmerLoader} />}
          {results.map(result => (
            <ClassificationResultItemV2 key={result.id} _classifiedText={result.text || ''} _promptVersion="default" result={result} />
          ))}
          {emptyState}
        </div>
      ) : (
        <div className={styles.container}>
          <Card>
            {isLoading && <Shimmer className={styles.shimmerLoader} />}
            {results.map(result => (
              <ClassificationResultItem key={result.id} _classifiedText={result.text || ''} _promptVersion="default" result={result} />
            ))}
            {emptyState}
          </Card>
        </div>
      )}
    </div>
  );
}
